"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
function EstimateFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="148757" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-[#66CCFF]/10 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="h-8 w-8 text-[#66CCFF]" />
        </div>
        <h3 className="font-sans text-2xl font-bold text-[#212B36] mb-2">
          Request Received
        </h3>
        <p className="text-[#637381] mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-[8px] px-8 border-[#DFE2E5] text-[#637381]">
          Submit Another Request
        </Button>
      </div>;
  }
  return <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-[#212B36] mb-2 tracking-tight">
          Accident Report Intake
        </h2>
        <p className="text-[#637381] text-sm md:text-base">
          Step 1 of 5: Find Your Crash Report
        </p>
        <div className="mt-6 w-full bg-[#DFE2E5] h-1.5 rounded-full overflow-hidden">
          <div className="bg-[#66CCFF] h-full rounded-full transition-all duration-700 ease-out" style={{
          width: '20%'
        }} />
        </div>
      </div>

      <Form onSubmit={handleSubmit} className="space-y-6">
        <FormField control={control} name="accident_location" rules={{
        required: "Accident location is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-[#637381] font-bold">Accident Location</FormLabel>
              <FormControl>
                <Input placeholder="e.g., 123 Main St, Springfield" className="h-12 rounded-[8px] bg-white border-[#DFE2E5] focus-visible:border-[#66CCFF] focus-visible:ring-[#66CCFF] transition-colors" {...field} />
              </FormControl>
              <FormDescription className="text-[11px] text-[#637381]/70">
                Enter the exact address or description of the location.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="accident_date" rules={{
          required: "Date of accident is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-[#637381] font-bold">Date of Accident</FormLabel>
                <FormControl>
                  <Input type="date" placeholder="MM/DD/YYYY" className="h-12 rounded-[8px] bg-white border-[#DFE2E5] focus-visible:border-[#66CCFF] focus-visible:ring-[#66CCFF] transition-colors" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="name" rules={{
          required: "Your name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-[#637381] font-bold">Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" className="h-12 rounded-[8px] bg-white border-[#DFE2E5] focus-visible:border-[#66CCFF] focus-visible:ring-[#66CCFF] transition-colors" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-[#637381] font-bold">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" className="h-12 rounded-[8px] bg-white border-[#DFE2E5] focus-visible:border-[#66CCFF] focus-visible:ring-[#66CCFF] transition-colors" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-xs uppercase tracking-wider text-[#637381] font-bold">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(555) 123-4567" className="h-12 rounded-[8px] bg-white border-[#DFE2E5] focus-visible:border-[#66CCFF] focus-visible:ring-[#66CCFF] transition-colors" {...field} />
                </FormControl>
                <FormDescription className="text-[11px] text-[#637381]/70">
                  Include country code if outside the US.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-[8px] flex items-start gap-3 text-destructive">
            <CircleAlert className="h-5 w-5 shrink-0 mt-0.5" />
            <p className="text-sm">{submitError}</p>
          </div>}

        <div className="pt-2">
          <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-bold rounded-[8px] bg-[#DFE2E5] text-[#A5B0BA] hover:bg-[#66CCFF] hover:text-[#212B36] transition-all shadow-sm">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 h-5 w-5" />
                Searching...
              </> : "Next Step"}
          </Button>

          <p className="mt-6 text-center text-[13px] text-[#637381] leading-relaxed">
            By clicking "Next Step", you agree to our{" "}
            <Link to="/terms" className="text-[#FF6600] hover:underline font-semibold">
              terms of Use
            </Link>{" "}
            and Privacy Policy. We only use your information to help locate your official accident report.
          </p>
        </div>
      </Form>
    </div>;
}
export default function InputText() {
  return <section id="intakeform" className="py-24 bg-[#212B36] relative overflow-hidden flex items-center justify-center min-h-[80vh]">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-[#66CCFF]/5 rounded-full blur-3xl -z-10 pointer-events-none" />
      
      <div className="container mx-auto px-4 w-full flex justify-center">
        <div className="bg-white border border-[#DFE2E5] rounded-[8px] p-8 md:p-12 shadow-2xl shadow-black/40 relative backdrop-blur-sm w-full max-w-[700px]">
          <FormProvider formId="intake_form_home" sectionName="IntakeForm" defaultValues={{
          accident_location: "",
          accident_date: "",
          name: "",
          email: "",
          phone: ""
        }} validationRules={{
          accident_location: {
            required: true
          },
          accident_date: {
            required: true
          },
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          }
        }} submitText="Next Step" successMessage="We have received your request and will contact you shortly with your report details or any follow-up questions.">
            <EstimateFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}